import { assert } from '@fe/core';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { v4 } from 'uuid';
import type { RootState } from '.';

import { AppState, IntegrationStatus } from '../../../common/enums';
import { MyFleetAcidListItem } from '../../../components/my-fleet/types';
import { UpdateStats } from '../../../flights/interop/update-stats';
import { commonEventDefinitions } from '../../../monitoring/alerts/event-list/models';
import { PopupAlertItem } from '../../../monitoring/alerts/popup/popup-alert-service';
import {
    commonAircraftThresholdsDisplayOrder,
    commonAirportThresholdsDisplayOrder,
    defaultAircraftMonitorOptions,
    defaultAircraftThresholds,
    defaultAirportMonitorOptions,
    defaultAirportOptions,
    defaultAirportThresholds
} from '../../../monitoring/monitor-defaults';
import { TutorialType } from '../../../onboarding/steps';
import { AppStateArgs } from '../../../services/actor-messages';
import {
    AircraftThresholdItemModel,
    AircraftThresholdLevelModel,
    AirportThresholdGroupModel as ApiAirportThresholdGroupModel,
    AirportThresholdItemModel as ApiAirportThresholdItemModel,
    AirportThresholdSettingsItemModel as ApiAirportThresholdSettingsItemModel,
    CompanyMonitorSettingsSet as ApiCompanyMonitorSettingsSet,
    FEMonitorSettingsModel as ApiFEMonitorSettingsModel,
    UserMonitorSettingsSet as ApiUserMonitorSettingsSet,
    BaseMapModelV2,
    MonitorSettingsService,
    NavMapModelV2
} from '../../../services/axios/axios-api-v2-generated';
import { SystemHubImportanceLevel, SystemHubNotification } from '../../../types/systemhub-notification';
import {
    AirportThresholdGroupUsage,
    commonAircraftThresholdDefinitions,
    commonAirportThresholdDefinitions,
    CompanyThresholdsMode,
    MonitorAircraftSettingsSet,
    MonitorAircraftThreshold,
    MonitorAircraftThresholdAlerts,
    MonitorAirportSettingsSet,
    MonitorAirportThreshold,
    MonitorAirportThresholdSettingsItem,
    MonitorAirportThresholdsGroupModel,
    MonitorCompanySet,
    MonitorSet,
    MonitorSettings,
    MonitorThresholdLevelModel,
    MonitorUserSet,
    ThresholdControlType,
    ThresholdEnableState,
    ThresholdInfo,
    ThresholdLevelActionType,
    ThresholdsCustomEventEntry,
    ThresholdWorseningDirection
} from '../../../types/thresholds';
import feVersion from '../../../utils/version-helper';
import {
    changeSpeedPlayback,
    pausePlayback,
    playbackProgressChanged,
    playbackSeekEnded,
    playbackSeekStart,
    resumePlayback,
    startPlayback,
    stopPlayback
} from '../reducers/playback';
import { startRecording, stopRecording } from '../reducers/recording';

interface RecordingNotActiveState {
    active: false;
}

interface RecordingActiveState {
    active: true;
    fileName: string;
}

interface PlaybackActiveState {
    active: true;
    name: string;
    speed: number;
    pause: boolean;
    currentTime: number;
    startTime: number;
    endTime: number;
    duration: number;
    snapshots: { time: number; optional: boolean }[];
    seek: { targetTime: number } | false;
}

interface PlaybackNotActiveState {
    active: false;
}

export interface NavMapModel {
    company: string;
    buildDate: number;
    version: number;
    sequence: number;
    cycleDate: number;
}

export interface BaseMapModel {
    buildDate: number;
    version: number;
}

export interface GlobalSlice {
    mapSettings: {
        navSources: NavMapModel[];
        baseSources: BaseMapModel[];
        tileHost: string;
    };
    recording: RecordingNotActiveState | RecordingActiveState;
    playback: PlaybackActiveState | PlaybackNotActiveState;
    notifications: NotificationStateItem[];
    flightWorker: {
        state: AppStateArgs;
        myAirports: string[];
        myFleetLoaded: boolean;
        lastUpdate: (UpdateStats & { time: number }) | null;
    };
    integrationState: {
        status: IntegrationStatus;
        desktopAppName: string;
    };
    myFleetFileAcidList: MyFleetAcidListItem[];
    firstSyncCompleted: boolean;
    monitorSettings: MonitorSettings;
    popupAlerts: PopupAlertItem[];
    fullscreen: boolean;
    slideshow: boolean;
    systemHubNotification: SystemHubNotification;
    tutorial: {
        scenarioId: TutorialType;
    } | null;
    network: {
        offline: boolean;
    };
    obfuscateFlights: null | {
        from: string;
        to: string;
    };
}

export enum NotificationType {
    noRecordingDir,
    weatherRecordingDirError,
    noMyFleetFile,
    myFileFileParseError,
    playbackFail,
    imageSaveInProgress,
    generalSuccess,
    generalError,
    generalWarning,
    generalInfo,
    udViewsImportSuccess,
    udViewsImportFailed,
    udViewSaveError,
    filterGroupSaveError,
    thresholdSaveError,
    emailAlertSendFail,
    emailAlertNoEmailAddress,
    emailAlertTooManyRequest,
    emailAlertGeneralError,
    routeDecodeError,
    playbackMissingWeatherDirectory,
    integrationInProgress,
    integrationRoutesReceived
}

export interface NotificationStateItem {
    id: string;
    closable: boolean;
    time: number;
    type: NotificationType;
    disposeAfterSeconds?: number | null; // Defaults to 15s, not disposed if set to null
    customMessage?: string;
}

const initialState: GlobalSlice = {
    mapSettings: {
        baseSources: [],
        navSources: [],
        tileHost: ''
    },
    recording: {
        active: false
    },
    playback: {
        active: false
    },
    flightWorker: {
        state: {
            type: AppState.Starting
        },
        myAirports: [],
        myFleetLoaded: false,
        lastUpdate: null
    },
    integrationState: {
        status: IntegrationStatus.NoRights,
        desktopAppName: 'Desktop interface'
    },
    notifications: [],
    myFleetFileAcidList: [],
    firstSyncCompleted: false,
    monitorSettings: {
        user: {
            aircraft: {
                thresholds: [],
                options: defaultAircraftMonitorOptions
            },
            airport: {
                groups: [],
                thresholdsSettings: [],
                options: defaultAirportMonitorOptions
            }
        },
        company: {
            aircraft: {
                thresholds: [],
                options: defaultAircraftMonitorOptions
            },
            airport: {
                groups: [],
                thresholdsSettings: [],
                options: defaultAirportMonitorOptions
            },
            mode: CompanyThresholdsMode.notUsed
        },
        eventDefinitions: commonEventDefinitions,
        thresholds: {
            aircraft: {
                displayOrder: commonAircraftThresholdsDisplayOrder,
                definitions: commonAircraftThresholdDefinitions
            },
            airport: {
                displayOrder: commonAirportThresholdsDisplayOrder,
                definitions: commonAirportThresholdDefinitions
            }
        }
    },
    popupAlerts: [],
    fullscreen: false,
    slideshow: false,
    systemHubNotification: {
        importance: SystemHubImportanceLevel.none,
        showPopup: false,
        text: null
    },
    tutorial: null,
    network: {
        offline: false
    },
    obfuscateFlights: null
};

export const globalSlice = createSlice({
    name: 'global',
    initialState: initialState,
    reducers: {
        setMapUrlReplacement(
            state,
            params: PayloadAction<{ tileHost: string; baseSources: BaseMapModelV2[]; navSources: NavMapModelV2[] }>
        ) {
            state.mapSettings.tileHost = params.payload.tileHost;
            const baseSources = params.payload.baseSources
                .map(
                    (x) =>
                        ({
                            buildDate: dayjs(x.buildDate, { utc: true }).unix(),
                            version: x.version ?? 0
                        }) satisfies BaseMapModel
                )
                .sort((a, b) => b.buildDate - a.buildDate || a.version - b.version);
            const navSource = params.payload.navSources
                .map(
                    (x) =>
                        ({
                            company: x.company ?? '',
                            buildDate: dayjs(x.buildDate, { utc: true }).unix(),
                            version: x.version ?? 0,
                            cycleDate: dayjs(x.cycleDate, { utc: true }).unix(),
                            sequence: x.sequence ?? 0
                        }) satisfies NavMapModel
                )
                .sort((a, b) => b.buildDate - a.buildDate || a.sequence - b.sequence);

            state.mapSettings.baseSources = baseSources;
            state.mapSettings.navSources = navSource;
        },
        addNotification: {
            reducer(state, action: PayloadAction<NotificationStateItem>) {
                state.notifications.unshift(action.payload);
            },
            //Allows us to later peek at id to dismiss by hand/transform
            prepare(params: Omit<NotificationStateItem, 'id' | 'time' | 'closable'> & { closable?: boolean }) {
                const id = v4();
                return {
                    payload: {
                        id,
                        time: dayjs().unix(),
                        closable: true,
                        ...params
                    }
                };
            }
        },
        dismissNotification(state, params: { payload: { id: string } }) {
            state.notifications = state.notifications.filter((x) => x.id !== params.payload.id);
        },
        transformNotification(state, params: { payload: Omit<NotificationStateItem, 'time' | 'closable'> & { closable?: boolean } }) {
            const notification = state.notifications.find((n) => n.id === params.payload.id);
            if (notification) {
                notification.type = params.payload.type;
                notification.time = dayjs().unix();
                notification.closable = params.payload.closable ?? true;
                if (params.payload.customMessage !== undefined) {
                    notification.customMessage = params.payload.customMessage;
                }
                notification.disposeAfterSeconds = params.payload.disposeAfterSeconds;
            }
        },
        updateMyFleetFileAcidList(state, params: { payload: MyFleetAcidListItem[] }) {
            state.myFleetFileAcidList = params.payload;
        },
        updateThresholdCompanyMode(state, params: PayloadAction<CompanyThresholdsMode>) {
            state.monitorSettings.company.mode = params.payload;
        },
        setMonitorSettings(state, params: PayloadAction<ApiFEMonitorSettingsModel>) {
            const model = fromApiModel(params.payload);
            // If user monitor settings were null we should set them to defaults
            if (!params.payload.user) {
                model.user.aircraft.thresholds = defaultAircraftThresholds;
                model.user.airport.groups = [
                    {
                        airportId: '',
                        usage: AirportThresholdGroupUsage.default,
                        autoGenerated: false,
                        isOff: false,
                        thresholds: defaultAirportThresholds
                    }
                ];
                model.user.airport.thresholdsSettings = defaultAirportOptions;

                model.user.aircraft.options = defaultAircraftMonitorOptions;
                model.user.airport.options = defaultAirportMonitorOptions;
            }

            state.monitorSettings.user = model.user;
            state.monitorSettings.company = model.company;
        },
        workerUpdateState(state, params: { payload: { state: AppStateArgs } }) {
            state.flightWorker.state = params.payload.state;
        },
        workerFlightsUpdated(state, params: { payload: { stats: UpdateStats; time: number } }) {
            state.flightWorker.lastUpdate = {
                ...params.payload.stats,
                time: params.payload.time
            };
        },
        workerSetMyFleetLoading(state, params: { payload: { loading: boolean } }) {
            state.flightWorker.myFleetLoaded = !params.payload.loading;
        },
        setFirstSyncCompleted(state) {
            state.firstSyncCompleted = true;
        },
        addPopupAlerts(state, action: PayloadAction<{ alerts: PopupAlertItem[] }>) {
            state.popupAlerts.push(...action.payload.alerts);
        },
        dismissPopupAlerts(state) {
            state.popupAlerts = [];
        },
        setIntegrationState(state, params: PayloadAction<{ status: IntegrationStatus; desktopAppName?: string }>) {
            state.integrationState.status = params.payload.status;
            if (params.payload.desktopAppName && params.payload.desktopAppName.trim().length > 0) {
                state.integrationState.desktopAppName = params.payload.desktopAppName;
            }
        },
        setFullscreen(state, params: PayloadAction<{ enabled: boolean }>) {
            state.fullscreen = params.payload.enabled;
        },
        setSlideshow(state, params: PayloadAction<{ active: boolean }>) {
            state.slideshow = params.payload.active;
        },
        setSystemHubNotification(state, params: PayloadAction<{ notification: SystemHubNotification }>) {
            state.systemHubNotification = params.payload.notification;
        },
        setTutorial(state, params: PayloadAction<{ scenarioId: TutorialType } | null>) {
            state.tutorial = params.payload;
        },
        setCustomEvents(state, params: PayloadAction<ThresholdsCustomEventEntry[]>) {
            // Filter for only codes >= 10000 so that we don't clash with regular thresholds and events
            const customEvents: ThresholdsCustomEventEntry[] = [];
            // Make sure there are no duplicates in the list (technically can happen because of code range overlap between providers)
            for (const event of params.payload) {
                if (!customEvents.some((x) => x.code === event.code)) {
                    customEvents.push(event);
                }
            }

            state.monitorSettings.eventDefinitions = {
                ...Object.fromEntries(customEvents.map((x) => [x.code, x])),
                ...commonEventDefinitions
            };
            state.monitorSettings.thresholds.aircraft.displayOrder = [
                ...commonAircraftThresholdsDisplayOrder,
                // Use set to make sure there are not duplicates, and
                ...customEvents.map((x) => x.code)
            ];
            state.monitorSettings.thresholds.aircraft.definitions = {
                ...commonAircraftThresholdDefinitions,
                // Turn every custom event into an alert only thresholds
                ...Object.fromEntries(
                    customEvents.map((x) => [
                        x.code,
                        {
                            name: x.displayName,
                            worseningDirection: ThresholdWorseningDirection.none,
                            decimal: false,
                            canRepeat: false,
                            min: 0,
                            max: 0,
                            alertsOnly: true,
                            disabled: false,
                            controlType: ThresholdControlType.checkbox
                        } satisfies ThresholdInfo
                    ])
                )
            };
        },
        setNetworkStatus(state, params: PayloadAction<{ offline: boolean }>) {
            state.network.offline = params.payload.offline;
        },
        setObfuscateFlights(state, params: PayloadAction<GlobalSlice['obfuscateFlights']>) {
            state.obfuscateFlights = params.payload;
        },
        setMyAirports(state, params: PayloadAction<string[]>) {
            state.flightWorker.myAirports = params.payload;
        }
    },

    extraReducers: (builder) => {
        builder.addCase(updateMonitorSettings.fulfilled, (state, action) => {
            if (!action.payload) {
                return;
            }

            const updates = action.meta.arg;
            if (updates.user) {
                if (updates.user.aircraft) {
                    Object.assign(state.monitorSettings.user.aircraft, updates.user.aircraft);
                }
                if (updates.user.airport) {
                    Object.assign(state.monitorSettings.user.airport, updates.user.airport);
                }
            }

            if (updates.company) {
                state.monitorSettings.company.mode = updates.company.companyMode;
                if (updates.company.aircraft) {
                    Object.assign(state.monitorSettings.company.aircraft, updates.company.aircraft);
                }
                if (updates.company.airport) {
                    Object.assign(state.monitorSettings.company.airport, updates.company.airport);
                }
            }
        });

        builder.addCase(startRecording.fulfilled, (state, action) => {
            state.recording = {
                active: true,
                fileName: action.payload.fileName
            };
        });

        builder.addCase(stopRecording.fulfilled, (state) => {
            state.recording = {
                active: false
            };
        });

        builder.addCase(startPlayback.fulfilled, (state, action) => {
            if (!action.payload.ok) {
                return;
            }
            state.playback = {
                active: true,
                speed: 1,
                pause: false,
                seek: false,
                currentTime: action.payload.startTime,
                startTime: action.payload.startTime,
                endTime: action.payload.endTime,
                duration: action.payload.endTime - action.payload.startTime,
                snapshots: action.payload.snapshots,
                name: action.meta.arg.file.name
            };
        });

        builder.addCase(stopPlayback.fulfilled, (state) => {
            state.playback = {
                active: false
            };
        });

        builder.addCase(pausePlayback.fulfilled, (state) => {
            assert(state.playback.active);
            state.playback.pause = true;
        });

        builder.addCase(resumePlayback.fulfilled, (state) => {
            assert(state.playback.active);
            state.playback.pause = false;
        });

        builder.addCase(changeSpeedPlayback.fulfilled, (state, action) => {
            assert(state.playback.active);
            state.playback.speed = action.meta.arg.speed;
        });

        builder.addCase(playbackProgressChanged, (state, action) => {
            if (state.playback.active) {
                state.playback.currentTime = action.payload.currentTime;
            }
        });

        builder.addCase(playbackSeekStart.fulfilled, (state, action) => {
            if (state.playback.active) {
                state.playback.seek = { targetTime: action.meta.arg.toTime };
            }
        });

        builder.addCase(playbackSeekEnded, (state) => {
            if (state.playback.active) {
                state.playback.seek = false;
            }
        });
    }
});

export const {
    setMapUrlReplacement,
    addNotification,
    dismissNotification,
    transformNotification,
    updateMyFleetFileAcidList,
    updateThresholdCompanyMode,
    setMonitorSettings,
    workerFlightsUpdated,
    workerSetMyFleetLoading,
    workerUpdateState,
    setFirstSyncCompleted,
    addPopupAlerts,
    dismissPopupAlerts,
    setIntegrationState,
    setFullscreen,
    setSlideshow,
    setSystemHubNotification,
    setTutorial,
    setCustomEvents,
    setNetworkStatus,
    setObfuscateFlights,
    setMyAirports
} = globalSlice.actions;

export const updateMonitorSettings = createAsyncThunk(
    'global/save-thresholds',
    async (
        params: {
            user?: { aircraft?: Partial<MonitorAircraftSettingsSet>; airport?: Partial<MonitorAirportSettingsSet> };
            company?: {
                aircraft?: Partial<MonitorAircraftSettingsSet>;
                airport?: Partial<MonitorAirportSettingsSet>;
                companyMode: CompanyThresholdsMode;
            };
        },
        thunkApi
    ) => {
        if (!params.user && !params.company) {
            return;
        }

        const slice = (thunkApi.getState() as RootState).global;

        try {
            await MonitorSettingsService.modifyAsync(
                {
                    body: {
                        user: params.user
                            ? toApiModelThresholds({
                                  airport: {
                                      ...slice.monitorSettings.user.airport,
                                      ...params.user.airport
                                  },
                                  aircraft: {
                                      ...slice.monitorSettings.user.aircraft,
                                      ...params.user.aircraft
                                  }
                              })
                            : undefined,
                        company: params.company
                            ? toApiModelThresholdsCompany({
                                  mode: params.company.companyMode,
                                  airport: {
                                      ...slice.monitorSettings.company.airport,
                                      ...params.company.airport
                                  },
                                  aircraft: {
                                      ...slice.monitorSettings.company.aircraft,
                                      ...params.company.aircraft
                                  }
                              })
                            : undefined
                    }
                },
                { withAuthorization: true }
            );
        } catch {
            thunkApi.dispatch(addNotification({ type: NotificationType.thresholdSaveError }));
            return thunkApi.rejectWithValue(true);
        }

        return thunkApi.fulfillWithValue(true);
    }
);

function fromApiModel(payload: ApiFEMonitorSettingsModel): Pick<MonitorSettings, 'user' | 'company'> {
    return {
        user: fromApiModelThresholds(payload.user),
        company: {
            ...fromApiModelThresholds(payload.company),
            mode: payload.company?.companyMode ?? CompanyThresholdsMode.notUsed
        }
    };
}

function fromApiModelThresholds(model: ApiUserMonitorSettingsSet | ApiCompanyMonitorSettingsSet | undefined): MonitorSet {
    return {
        originalDbVersion: model?.originalDbFEWebVersion,
        originalFeVersion: model?.originalFEWebVersion,
        airport: {
            groups:
                model?.airportThresholds?.thresholds?.map(
                    (x) =>
                        ({
                            airportId: x.airportId ?? '',
                            usage: x.usage ?? AirportThresholdGroupUsage.default,
                            autoGenerated: x.autoGenerated ?? false,
                            isOff: x.isOff ?? false,
                            thresholds:
                                x.thresholds?.map(
                                    (x) =>
                                        ({
                                            type: x.type!,
                                            enableState: x.enableState ?? ThresholdEnableState.notSet,
                                            info: thresholdLevelFromApiModel(x.info) ?? null,
                                            watch: thresholdLevelFromApiModel(x.watch) ?? null,
                                            warning: thresholdLevelFromApiModel(x.warning) ?? null,
                                            critical: thresholdLevelFromApiModel(x.critical) ?? null,
                                            emergency: thresholdLevelFromApiModel(x.emergency) ?? null,
                                            ignoredInheritedLevels: x.offInheritedLevels ?? []
                                        }) satisfies MonitorAirportThreshold
                                ) ?? []
                        }) satisfies MonitorAirportThresholdsGroupModel
                ) ?? [],
            thresholdsSettings:
                model?.airportThresholds?.options?.map(
                    (x) =>
                        ({
                            type: x.type!,
                            // TODO: priority should be nullable on the api side I think to allow inheritance
                            priority: x.priority ?? 50,
                            alerts: {
                                eventList: {
                                    value: x.alerts?.eventList ?? null,
                                    minLevel: x.alerts?.eventListMinLevel ?? null
                                },
                                eventListAck: {
                                    value: x.alerts?.eventListAck ?? null,
                                    minLevel: x.alerts?.eventListAckMinLevel ?? null
                                },
                                eventListImportant: {
                                    value: x.alerts?.eventListImportant ?? null,
                                    minLevel: x.alerts?.eventListImportantMinLevel ?? null
                                },
                                email: {
                                    value: x.alerts?.eMail ?? null,
                                    minLevel: x.alerts?.eMailMinLevel ?? null
                                },
                                logFile: {
                                    value: x.alerts?.logFile ?? null,
                                    minLevel: x.alerts?.logFileMinLevel ?? null
                                },
                                popup: {
                                    value: x.alerts?.popup ?? null,
                                    minLevel: x.alerts?.popupMinLevel ?? null
                                },
                                osAlert: {
                                    value: x.alerts?.osAlert ?? null,
                                    minLevel: x.alerts?.osAlertMinLevel ?? null
                                },
                                overrideColor: {
                                    value: x.alerts?.overrideColor ?? null,
                                    minLevel: x.alerts?.overrideColorMinLevel ?? null
                                },
                                voice: {
                                    value: x.alerts?.voice ?? null,
                                    minLevel: x.alerts?.voiceMinLevel ?? null
                                },
                                playSound: {
                                    value: x.alerts?.playSound ?? null,
                                    minLevel: x.alerts?.playSoundMinLevel ?? null
                                },
                                overrideColorHex: x.alerts?.overrideColorHex ?? null
                            }
                        }) satisfies MonitorAirportThresholdSettingsItem
                ) ?? [],
            options: {
                tafWindowHours: model?.monitorOptions?.tafWindowHours ?? null,
                airportMonitorStatusInclude: {
                    originThresholds: model?.monitorOptions?.airportMonitorStatusInclude?.originThresholds ?? null,
                    destinationThresholds: model?.monitorOptions?.airportMonitorStatusInclude?.destinationThresholds ?? null,
                    alternateThresholds: model?.monitorOptions?.airportMonitorStatusInclude?.alternateThresholds ?? null
                }
            }
        },
        aircraft: {
            thresholds:
                model?.aircraftThresholds?.map(
                    (x) =>
                        ({
                            type: x.type!,
                            priority: x.priority ?? 50,
                            enableState: x.enableState ?? ThresholdEnableState.notSet,
                            info: thresholdLevelFromApiModel(x.info) ?? null,
                            watch: thresholdLevelFromApiModel(x.watch) ?? null,
                            warning: thresholdLevelFromApiModel(x.warning) ?? null,
                            critical: thresholdLevelFromApiModel(x.critical) ?? null,
                            emergency: thresholdLevelFromApiModel(x.emergency) ?? null,
                            alerts: {
                                eventList: {
                                    value: x.alerts?.eventList ?? null,
                                    minLevel: x.alerts?.eventListMinLevel ?? null
                                },
                                eventListAck: {
                                    value: x.alerts?.eventListAck ?? null,
                                    minLevel: x.alerts?.eventListAckMinLevel ?? null
                                },
                                eventListImportant: {
                                    value: x.alerts?.eventListImportant ?? null,
                                    minLevel: x.alerts?.eventListImportantMinLevel ?? null
                                },
                                email: {
                                    value: x.alerts?.eMail ?? null,
                                    minLevel: x.alerts?.eMailMinLevel ?? null
                                },
                                logFile: {
                                    value: x.alerts?.logFile ?? null,
                                    minLevel: x.alerts?.logFileMinLevel ?? null
                                },
                                popup: {
                                    value: x.alerts?.popup ?? null,
                                    minLevel: x.alerts?.popupMinLevel ?? null
                                },
                                osAlert: {
                                    value: x.alerts?.osAlert ?? null,
                                    minLevel: x.alerts?.osAlertMinLevel ?? null
                                },
                                aircraftTag: {
                                    value: x.alerts?.aircraftTag ?? null,
                                    minLevel: x.alerts?.aircraftTagMinLevel ?? null
                                },
                                zoomToAircraft: {
                                    value: x.alerts?.zoomToAircraft ?? null,
                                    minLevel: x.alerts?.zoomToAircraftMinLevel ?? null
                                },
                                overrideColor: {
                                    value: x.alerts?.overrideColor ?? null,
                                    minLevel: x.alerts?.overrideColorMinLevel ?? null
                                },
                                enableMarker: {
                                    value: x.alerts?.enableMarker ?? null,
                                    minLevel: x.alerts?.enableMarkerMinLevel ?? null
                                },
                                voice: {
                                    value: x.alerts?.voice ?? null,
                                    minLevel: x.alerts?.voiceMinLevel ?? null
                                },
                                playSound: {
                                    value: x.alerts?.playSound ?? null,
                                    minLevel: x.alerts?.playSoundMinLevel ?? null
                                },
                                overrideColorHex: x.alerts?.overrideColorHex ?? null,
                                alertOnScheduled: { value: x.alerts?.alertOnScheduled ?? null, minLevel: null },
                                alertOnPlanned: { value: x.alerts?.alertOnPlanned ?? null, minLevel: null },
                                alertOnActive: { value: x.alerts?.alertOnActive ?? null, minLevel: null }
                            } satisfies MonitorAircraftThresholdAlerts,
                            ignoredInheritedLevels: x.ignoredTemplateLevels ?? []
                        }) satisfies MonitorAircraftThreshold
                ) ?? [],
            options: {
                monitorOriginDepartureMinMinutes: model?.monitorOptions?.monitorOriginDepartureMinMinutes ?? null,
                monitorOriginWithoutDepartureTime: model?.monitorOptions?.monitorOriginWithoutDepartureTime ?? null,
                monitorDestinationDepartureMinMinutes: model?.monitorOptions?.monitorDestinationDepartureMinMinutes ?? null,
                monitorDestinationWithoutArrivalTime: model?.monitorOptions?.monitorDestinationWithoutArrivalTime ?? null,
                monitorAltDepartureMinMinutes: model?.monitorOptions?.monitorAltDepartureMinMinutes ?? null,
                monitorAltWithoutArrivalTime: model?.monitorOptions?.monitorAltWithoutArrivalTime ?? null,
                emergencyBeacons:
                    model?.monitorOptions?.emergencyBeacons?.map((x) => ({
                        code: x.code ?? 0,
                        text: x.text ?? ''
                    })) ?? [],
                ignoredCompanyBeaconCodes: model?.monitorOptions?.ignoredCompanyBeaconCodes ?? [],
                altitudeChangeWindowMinutes: model?.monitorOptions?.altitudeChangeWindowMinutes ?? null,
                suppressAltitudeMonitorMiles: model?.monitorOptions?.suppressAltitudeMonitorMiles ?? null,
                monitorImpactDistance: {
                    volcanicAsh: model?.monitorOptions?.monitorImpactDistance?.volcanicAsh ?? null,
                    sigmet: model?.monitorOptions?.monitorImpactDistance?.sigmet ?? null,
                    convSigment: model?.monitorOptions?.monitorImpactDistance?.convSigment ?? null,
                    dsua: model?.monitorOptions?.monitorImpactDistance?.dsua ?? null,
                    edrTurbulence: model?.monitorOptions?.monitorImpactDistance?.edrTurbulence ?? null,
                    turbulence: model?.monitorOptions?.monitorImpactDistance?.turbulence ?? null,
                    icing: model?.monitorOptions?.monitorImpactDistance?.icing ?? null,
                    contrail: model?.monitorOptions?.monitorImpactDistance?.contrail ?? null,
                    tcf: model?.monitorOptions?.monitorImpactDistance?.tcf ?? null
                }
            }
        }
    };
}

function thresholdLevelFromApiModel(model?: AircraftThresholdLevelModel): MonitorThresholdLevelModel | null {
    if (!model) {
        return null;
    }

    return {
        repeat: model.repeat ?? null,
        triggerOnImproving: model.triggerOnImproving ?? null,
        value: model.value ?? 0,
        unitIndex: model.unitIndex ?? null,
        actionType: model.actionType ?? ThresholdLevelActionType.none,
        actionTimeMinutes: model.actionTimeMinutes ?? null
    };
}

function toApiModelThresholdsCompany(model: MonitorCompanySet): ApiCompanyMonitorSettingsSet {
    return {
        Type: 1,
        originalDbFEWebVersion: model.originalDbVersion,
        originalFEWebVersion: model.originalFeVersion,
        companyMode: model.mode,
        ...toApiModelThresholds(model)
    };
}

function toApiModelThresholds(model: MonitorUserSet | MonitorCompanySet): ApiUserMonitorSettingsSet | ApiCompanyMonitorSettingsSet {
    return {
        Type: 0,
        currentDbFEWebVersion: feVersion.databaseVersion,
        currentFEWebVersion: feVersion.version,
        originalFEWebVersion: model.originalFeVersion,
        originalDbFEWebVersion: model.originalDbVersion,
        monitorOptions: {
            monitorOriginDepartureMinMinutes: model.aircraft.options.monitorOriginDepartureMinMinutes ?? undefined,
            monitorOriginWithoutDepartureTime: model.aircraft.options.monitorOriginWithoutDepartureTime ?? undefined,
            monitorDestinationDepartureMinMinutes: model.aircraft.options.monitorDestinationDepartureMinMinutes ?? undefined,
            monitorDestinationWithoutArrivalTime: model.aircraft.options.monitorDestinationWithoutArrivalTime ?? undefined,
            monitorAltDepartureMinMinutes: model.aircraft.options.monitorAltDepartureMinMinutes ?? undefined,
            monitorAltWithoutArrivalTime: model.aircraft.options.monitorAltWithoutArrivalTime ?? undefined,
            emergencyBeacons: model.aircraft.options.emergencyBeacons.map((x) => ({ code: x.code, text: x.text })),
            ignoredCompanyBeaconCodes: model.aircraft.options.ignoredCompanyBeaconCodes,
            altitudeChangeWindowMinutes: model.aircraft.options.altitudeChangeWindowMinutes ?? undefined,
            suppressAltitudeMonitorMiles: model.aircraft.options.suppressAltitudeMonitorMiles ?? undefined,
            monitorImpactDistance: {
                volcanicAsh: model.aircraft.options.monitorImpactDistance.volcanicAsh ?? undefined,
                sigmet: model.aircraft.options.monitorImpactDistance.sigmet ?? undefined,
                convSigment: model.aircraft.options.monitorImpactDistance.convSigment ?? undefined,
                dsua: model.aircraft.options.monitorImpactDistance.dsua ?? undefined,
                edrTurbulence: model.aircraft.options.monitorImpactDistance.edrTurbulence ?? undefined,
                turbulence: model.aircraft.options.monitorImpactDistance.turbulence ?? undefined,
                icing: model.aircraft.options.monitorImpactDistance.icing ?? undefined,
                contrail: model.aircraft.options.monitorImpactDistance.contrail ?? undefined,
                tcf: model.aircraft.options.monitorImpactDistance.tcf ?? undefined
            },
            tafWindowHours: model.airport.options.tafWindowHours ?? undefined,
            airportMonitorStatusInclude: {
                originThresholds: model.airport.options.airportMonitorStatusInclude.originThresholds ?? undefined,
                destinationThresholds: model.airport.options.airportMonitorStatusInclude.destinationThresholds ?? undefined,
                alternateThresholds: model.airport.options.airportMonitorStatusInclude.alternateThresholds ?? undefined
            }
        },
        airportThresholds: {
            thresholds: model.airport.groups.map(
                (x) =>
                    ({
                        airportId: x.airportId === '' ? undefined : x.airportId,
                        usage: x.usage,
                        thresholds: x.thresholds.map(
                            (x) =>
                                ({
                                    type: x.type,
                                    enableState: x.enableState,
                                    info: toApiModelThresholdLevel(x.info),
                                    watch: toApiModelThresholdLevel(x.watch),
                                    warning: toApiModelThresholdLevel(x.warning),
                                    critical: toApiModelThresholdLevel(x.critical),
                                    emergency: toApiModelThresholdLevel(x.emergency),
                                    offInheritedLevels: x.ignoredInheritedLevels
                                }) satisfies ApiAirportThresholdItemModel
                        ),
                        autoGenerated: x.autoGenerated,
                        isOff: x.isOff
                    }) satisfies ApiAirportThresholdGroupModel
            ),
            options: model.airport.thresholdsSettings.map(
                (x) =>
                    ({
                        type: x.type,
                        priority: x.priority,
                        alerts: {
                            eventList: x.alerts.eventList.value ?? undefined,
                            eventListMinLevel: x.alerts.eventList?.minLevel ?? undefined,
                            eventListAck: x.alerts.eventListAck.value ?? undefined,
                            eventListAckMinLevel: x.alerts.eventListAck?.minLevel ?? undefined,
                            eventListImportant: x.alerts.eventListImportant.value ?? undefined,
                            eventListImportantMinLevel: x.alerts.eventListImportant?.minLevel ?? undefined,
                            eMail: x.alerts.email.value ?? undefined,
                            eMailMinLevel: x.alerts.email?.minLevel ?? undefined,
                            logFile: x.alerts.logFile.value ?? undefined,
                            logFileMinLevel: x.alerts.logFile?.minLevel ?? undefined,
                            popup: x.alerts.popup.value ?? undefined,
                            popupMinLevel: x.alerts.popup?.minLevel ?? undefined,
                            osAlert: x.alerts.osAlert.value ?? undefined,
                            osAlertMinLevel: x.alerts.osAlert?.minLevel ?? undefined,
                            overrideColor: x.alerts.overrideColor.value ?? undefined,
                            overrideColorMinLevel: x.alerts.overrideColor?.minLevel ?? undefined,
                            voice: x.alerts.voice.value ?? undefined,
                            voiceMinLevel: x.alerts.voice?.minLevel ?? undefined,
                            playSound: x.alerts.playSound.value ?? undefined,
                            playSoundMinLevel: x.alerts.playSound?.minLevel ?? undefined,
                            overrideColorHex: x.alerts.overrideColorHex ?? undefined
                        }
                    }) satisfies ApiAirportThresholdSettingsItemModel
            )
        },
        aircraftThresholds: model.aircraft.thresholds.map(
            (x) =>
                ({
                    type: x.type,
                    priority: x.priority,
                    enableState: x.enableState,
                    info: toApiModelThresholdLevel(x.info),
                    watch: toApiModelThresholdLevel(x.watch),
                    warning: toApiModelThresholdLevel(x.warning),
                    critical: toApiModelThresholdLevel(x.critical),
                    emergency: toApiModelThresholdLevel(x.emergency),
                    alerts: {
                        eventList: x.alerts.eventList.value ?? undefined,
                        eventListMinLevel: x.alerts.eventList.minLevel ?? undefined,
                        eventListAck: x.alerts.eventListAck.value ?? undefined,
                        eventListAckMinLevel: x.alerts.eventListAck.minLevel ?? undefined,
                        eventListImportant: x.alerts.eventListImportant.value ?? undefined,
                        eventListImportantMinLevel: x.alerts.eventListImportant.minLevel ?? undefined,
                        eMail: x.alerts.email.value ?? undefined,
                        eMailMinLevel: x.alerts.email.minLevel ?? undefined,
                        logFile: x.alerts.logFile.value ?? undefined,
                        logFileMinLevel: x.alerts.logFile.minLevel ?? undefined,
                        popup: x.alerts.popup.value ?? undefined,
                        popupMinLevel: x.alerts.popup.minLevel ?? undefined,
                        osAlert: x.alerts.osAlert.value ?? undefined,
                        osAlertMinLevel: x.alerts.osAlert.minLevel ?? undefined,
                        aircraftTag: x.alerts.aircraftTag.value ?? undefined,
                        aircraftTagMinLevel: x.alerts.aircraftTag.minLevel ?? undefined,
                        zoomToAircraft: x.alerts.zoomToAircraft.value ?? undefined,
                        zoomToAircraftMinLevel: x.alerts.zoomToAircraft.minLevel ?? undefined,
                        overrideColor: x.alerts.overrideColor.value ?? undefined,
                        overrideColorMinLevel: x.alerts.overrideColor.minLevel ?? undefined,
                        enableMarker: x.alerts.enableMarker.value ?? undefined,
                        enableMarkerMinLevel: x.alerts.enableMarker.minLevel ?? undefined,
                        voice: x.alerts.voice.value ?? undefined,
                        voiceMinLevel: x.alerts.voice.minLevel ?? undefined,
                        playSound: x.alerts.playSound.value ?? undefined,
                        playSoundMinLevel: x.alerts.playSound.minLevel ?? undefined,
                        overrideColorHex: x.alerts.overrideColorHex ?? undefined,
                        alertOnScheduled: x.alerts.alertOnScheduled.value ?? undefined,
                        alertOnPlanned: x.alerts.alertOnPlanned.value ?? undefined,
                        alertOnActive: x.alerts.alertOnActive.value ?? undefined
                    },
                    ignoredTemplateLevels: x.ignoredInheritedLevels
                }) satisfies AircraftThresholdItemModel
        )
    };
}

function toApiModelThresholdLevel(model: MonitorThresholdLevelModel | null): AircraftThresholdLevelModel | undefined {
    if (!model) {
        return undefined;
    }
    return {
        triggerOnImproving: model.triggerOnImproving ?? undefined,
        value: model.value,
        unitIndex: model.unitIndex ?? undefined,
        actionType: model.actionType,
        actionTimeMinutes: model.actionTimeMinutes ?? undefined
    };
}
